import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'

import { organizationFlowObject } from '@/app.constant'
import DashboardService from '@/services/DashboardService'
import { getStickerSets, updateOrganizationFlow } from '@/store/common/actions'
import {
  addEntityToKnowledgeGroup,
  deleteKnowledgeById,
} from '@/store/knowledge/actions'
import { getAllUserGroupOptions } from '@/store/notifications/actions'
import {
  createIntentFromManageAnswer,
  getAllUserGroups,
  getKnowledgeById,
  getParaPhrasesBasedOnIntent,
  localUserQuestionUpdate,
  updateIntent,
} from '@/store/questions/actions'
import {
  AllUserGroups,
  getIntent,
  isQuestionLoading,
} from '@/store/questions/selector'

import KGroupsTree from '@/components/UI/KGroupsTree'
import ConfirmationModal from '@/components/UI/ConfirmationModal/ConfirmationModal'
import Loading from '@/components/UI/Loading/Loading'
import PermissionWrapper from '@/components/UI/PermissionWrapper/PermissionWrapper'
import UserSaysTab from '../Knowledge/KnowledgeCreateFromScratch/partials/UserSaysTab'
import VirtualAssistantTab from '../Knowledge/KnowledgeCreateFromScratch/partials/VirtualAssistantTab'

const service = new DashboardService()

const AddToKnowledge = (props: any) => {
  const { t } = useTranslation('translation')
  const dispatch = useDispatch()
  const useLocatio = useLocation()
  const questionLoading = useSelector(isQuestionLoading)
  const allUserGroupsData = useSelector(AllUserGroups)
  const intent = useSelector(getIntent)
  const [isIntentUpdating, setIsIntentUpdating] = useState(false)
  const [buttonText, setButtonText] = useState('')
  const [activeTab, setActiveTab] = useState('usersays')
  const [selectedButtonTarget, setSelectedButtonTarget] = useState(null)
  const [hasAddedQuestion, setHasAddedQuestion] = useState(false)
  const [userSaysTarget, setUserSaysTarget] = useState(null)
  const [hasAddedResponse, setHasAddedResponse] = useState(false)
  const [selectedResponseTarget, setSelectedResponseTarget] = useState(null)
  const [paraPhrasesLoading, setParaPhrasesLoading] = useState(false)

  useEffect(() => {
    dispatch(
      updateOrganizationFlow({
        ...organizationFlowObject,
        prevRoute: '/knowledge',
        currentRoute: '/knowledge-create-from-scratch',
        nextRoute: '/knowledge/generate-questions',
        setup_completed: false,
      }),
    )

    dispatch(getAllUserGroups())
    dispatch(getStickerSets())
    dispatch(getAllUserGroupOptions()) // user groups

    setUserSaysTarget(null)
    setSelectedResponseTarget(null)
    setHasAddedQuestion(false)
    setHasAddedResponse(false)

    const query = new URLSearchParams(useLocatio.search)
    const group = query.get('group')
    setSelectedGroupId(group ? group : null)
    if (group) {
      dispatch(localUserQuestionUpdate('knowledge_group_id', group))
    }
    return () => {
      console.log('CLEANED UP')
      dispatch({
        type: 'FETCH_KNOWLEDGE_BY_ID_SUCCESS',
        payload: {
          intent: null,
        },
      })
    }
  }, [])

  useEffect(() => {
    if (props.questionAnswerData.intent_id && !props.addAnswerFromPopup) {
      const getSelectedIntent = async (selectedIntent: any) => {
        await dispatch(getKnowledgeById(selectedIntent))
        setIsIntentUpdating(false)
      }
      setIsIntentUpdating(true)
      getSelectedIntent(props.questionAnswerData.intent_id)
      return
    }

    dispatch({
      type: 'FETCH_KNOWLEDGE_BY_ID_SUCCESS',
      payload: {
        intent: {
          questions: props.questionAnswerData.text
            ? [
                {
                  text: props.questionAnswerData.text,
                },
              ]
            : [],
          responses: props.questionAnswerData?.post
            ? props.questionAnswerData?.post.length > 1
              ? props.questionAnswerData?.post.map((e: any) => {
                  let temp: any = {
                    text: e.text,
                    group_ids: [],
                  }
                  return temp
                })
              : [
                  {
                    text: props.questionAnswerData.post[0]?.text,
                    group_ids: [],
                  },
                ]
            : null,
          name: props.questionAnswerData.question,
          type: 3,
          is_input_disabled: null,
          tags: [],
          parent_id: null,
          _id: props.questionAnswerData.intent_id
            ? props.questionAnswerData.intent_id
            : null,
          answers_by_openAI: !props.addAnswerFromPopup,
          conversation_log_id:
            props.questionAnswerData.conversation_log_id || null,
        },
      },
    })
  }, [props.questionAnswerData.intent_id])

  // Levels

  /**
   * Used to sort data using API
   * @param param
   * @param direction
   */
  const [selectedGroupId, setSelectedGroupId] = useState(null)

  useEffect(() => {
    if (
      !questionLoading &&
      intent &&
      props.selectedIntent &&
      props.selectedIntent.existingRecord
    ) {
      const oldList = intent.questions ? [...intent.questions] : []
      oldList.unshift({ text: props.selectedIntent.existingRecord.question })
      dispatch(localUserQuestionUpdate('questions', oldList))

      const oldResponses = intent.responses ? [...intent.responses] : []
      oldResponses.unshift({
        text: props.selectedIntent.existingRecord.answer,
        group_ids: [],
      })
      dispatch(localUserQuestionUpdate('responses', oldResponses))
    }
  }, [questionLoading])

  //
  useEffect(() => {
    buttonTextHandler()
  }, [intent])

  // Create intent
  const createIntenthandler = async () => {
    // For creation
    if (
      !props.editMode &&
      activeTab === 'usersays' &&
      (!intent || !intent.questions || intent.questions.length === 0)
    ) {
      try {
        if (selectedButtonTarget) {
          const oldButtonsList = []
          oldButtonsList.push({
            ...selectedButtonTarget,
            name: selectedButtonTarget.name,
            type: selectedButtonTarget.type,
          })
          await dispatch(localUserQuestionUpdate('buttons', oldButtonsList))
          setSelectedButtonTarget(null)
        }

        if (userSaysTarget) {
          const oldList = []
          await oldList.unshift(userSaysTarget)
          await dispatch(localUserQuestionUpdate('questions', oldList))
          setUserSaysTarget(null)
          if (!intent || !intent.responses) {
            setHasAddedResponse(true)
            tabsChangeHandler('virtualassistant')
            return
          }
        }

        if (selectedResponseTarget) {
          const oldList =
            intent && intent.responses ? [...intent.responses] : []
          await oldList.unshift(selectedResponseTarget)
          await dispatch(localUserQuestionUpdate('responses', oldList))
        }

        await setHasAddedResponse(true)
        await setHasAddedQuestion(true)
        await dispatch(
          createIntentFromManageAnswer({
            ...intent,
            answers_by_openAI: true,
            conversation_log_id: props.questionAnswerData.conversation_log_id,
            unaswered_question_id: props.questionAnswerData.selectedAnswer
              ? props.questionAnswerData.selectedAnswer._id
              : null,
            suggested_answer_id: props.questionAnswerData.intent_id,
            author_id: props.questionAnswerData.author_id || null,
          }),
        )
        resetEditKnowledgehandler()
      } catch (e) {
        console.log('e', e)
      }
      return
    }

    // If questions but no responses
    if (
      !props.editMode &&
      activeTab === 'usersays' &&
      intent &&
      intent.questions &&
      intent.questions.length > 0
    ) {
      tabsChangeHandler('virtualassistant')
      return
    }

    if (
      !props.editMode &&
      activeTab === 'virtualassistant' &&
      intent &&
      intent.questions &&
      intent.questions.length > 0
    ) {
      tabsChangeHandler('level')
      return
    }

    // All good proceed
    setIsIntentUpdating(true)
    try {
      if (
        userSaysTarget &&
        userSaysTarget.text &&
        userSaysTarget.text.trim().length > 0
      ) {
        const oldList = []
        await oldList.unshift(userSaysTarget)
        await dispatch(localUserQuestionUpdate('questions', oldList))
      }

      if (selectedResponseTarget) {
        const oldList = intent && intent.responses ? [...intent.responses] : []
        await oldList.unshift(selectedResponseTarget)
        await dispatch(localUserQuestionUpdate('responses', oldList))
      }

      await setHasAddedResponse(true)
      await setHasAddedQuestion(true)

      let new_intent_id: any = null
      if (props.addAnswerFromPopup) {
        try {
          new_intent_id = await dispatch(
            createIntentFromManageAnswer(
              {
                ...intent,
                answers_by_openAI: false,
                type: 3,
                author_id: props.questionAnswerData.author_id || null,
              },
              true,
            ),
          )
        } catch (error: any) {
          console.log('err', error)
        }

        if (props.questionAnswerData.post) {
          for await (const key of props.questionAnswerData.post) {
            await service.setApproval(props.questionAnswerData._id, key._id, {
              intent_id: new_intent_id,
              approval_status: 'accepted',
            })
          }
          props?.onSaveSuccess?.()
        }

        if (
          !props.questionAnswerData.intent_id &&
          (!props.questionAnswerData.post ||
            props.questionAnswerData.post.length === 0)
        ) {
          await service.saveDiscussion(props.questionAnswerData._id, {
            answer_status: 'answered',
            intent_id: new_intent_id,
          })
          props?.onSaveSuccess?.()
        }
      } else {
        new_intent_id = await dispatch(
          createIntentFromManageAnswer({
            ...intent,
            answers_by_openAI: true,
            conversation_log_id: props.questionAnswerData.conversation_log_id,
            unaswered_question_id: props.questionAnswerData.selectedAnswer
              ? props.questionAnswerData.selectedAnswer._id
              : null,
            suggested_answer_id: props.questionAnswerData.intent_id,
            author_id: props.questionAnswerData.author_id || null,
          }),
        )
      }

      if (new_intent_id && selectedGroupId) {
        await dispatch(
          addEntityToKnowledgeGroup(selectedGroupId, 'intent', [new_intent_id]),
        )
      }
      resetEditKnowledgehandler()
    } catch (error: any) {
      console.log('error', error)
    }
    setIsIntentUpdating(false)
  }

  const isUserSaysActivityDetected = (e: any) => {
    setUserSaysTarget({
      text: e,
    })
  }

  const isResponseActivityDetected = (e: any) => {
    setSelectedResponseTarget(e)
  }

  const tabsChangeHandler = (e: any) => {
    setActiveTab(e)
    buttonTextHandler(e)
  }

  const buttonTextHandler = (tab = activeTab) => {
    if (!props.editMode) {
      if (tab === 'usersays') {
        setButtonText('Next')
      } else if (tab === 'level') {
        setButtonText('Save')
      }
    }
  }

  // Show delete modal
  const [modalShow, setModalShow] = useState(false)
  const modalhandler = (value: boolean) => {
    setModalShow(value)
  }

  const deleteConfirmed = async (
    e: React.ChangeEvent<HTMLInputElement>,
    selectedRowIds: string[],
  ) => {
    e.preventDefault()
    setModalShow(false)
    setIsIntentUpdating(true)
    await dispatch(deleteKnowledgeById(selectedRowIds, false))
    props.resetEditKnowledgehandler('reload')
    setIsIntentUpdating(false)
  }

  const updateIntenthandler = async () => {
    if (activeTab === 'level') {
      if (selectedGroupId) {
        setIsIntentUpdating(true)
        dispatch(
          addEntityToKnowledgeGroup(selectedGroupId, 'intent', [intent._id]),
        )
        setIsIntentUpdating(false)
      }
    } else {
      setIsIntentUpdating(true)
      await dispatch(updateIntent())
      setIsIntentUpdating(false)
      resetEditKnowledgehandler()
    }
  }

  const resetEditKnowledgehandler = () => {
    dispatch({
      type: 'FETCH_KNOWLEDGE_BY_ID_SUCCESS',
      payload: {
        intent: null,
      },
    })
    props.resetEditKnowledgehandler()
  }

  const getParaPhrasesBasedOnIntentHandler = async () => {
    try {
      setParaPhrasesLoading(true)
      const { questions, is_paraphrased }: any = await dispatch(
        getParaPhrasesBasedOnIntent(intent?._id),
      )
      setParaPhrasesLoading(false)

      let oldQuestions: any[] = []
      if (questions.length) {
        oldQuestions = [...questions]
      }
      await dispatch(localUserQuestionUpdate('questions', oldQuestions))
      await dispatch(localUserQuestionUpdate('is_paraphrased', is_paraphrased))
    } catch (error) {
      setParaPhrasesLoading(false)
      console.log('[ error ]', error)
    }
  }

  // questionLoading
  if (isIntentUpdating) {
    return <Loading />
  }

  return (
    <Box>
      {intent && intent._id && (
        <ConfirmationModal
          message={t(
            'You are deleting a question. Note that all the children of this question will also be deleted. Are you sure you want to delete?',
          )}
          size={'md'}
          show={modalShow}
          selectedRow={[intent._id]}
          onHide={() => modalhandler(false)}
          confirmHandler={deleteConfirmed}
        />
      )}

      <Box>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => tabsChangeHandler(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab value="usersays" label={t('Questions')} />
          <Tab
            value="virtualassistant"
            label={t('Answers')}
            disabled={
              !props.editMode &&
              intent &&
              intent.questions &&
              intent.questions.length === 0 &&
              allUserGroupsData.length > 0
            }
          />
          <Tab value="level" label={t('Group')} />
        </Tabs>

        {activeTab === 'usersays' && (
          <UserSaysTab
            activeTab={activeTab}
            editMode={props.editMode}
            isActivityDetected={isUserSaysActivityDetected}
            hasAdded={hasAddedQuestion}
          />
        )}

        {activeTab === 'virtualassistant' && (
          <VirtualAssistantTab
            allUserGroups={[...allUserGroupsData]}
            isActivityDetected={isResponseActivityDetected}
            hasAdded={hasAddedResponse}
            nodeElement={true}
          />
        )}

        {activeTab === 'level' && (
          <KGroupsTree
            actions={{ edit: true }}
            syncsState={false}
            onSelectGroup={group => {
              setSelectedGroupId(group?._id || null)
            }}
            initialSelectedGroupId={
              intent?.knowledge_group_ids?.length > 0
                ? intent.knowledge_group_ids[0]
                : null
            }
          />
        )}
      </Box>

      {/* Footer Actions */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 2,
          mt: 3,
          pt: 2,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        {paraPhrasesLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 'auto' }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="body2">
              {t('Generating training phrases...')}
            </Typography>
          </Box>
        )}

        {intent &&
          intent._id &&
          !paraPhrasesLoading &&
          !intent.is_paraphrased &&
          activeTab === 'usersays' && (
            <Box sx={{ mr: 'auto' }}>
              <Typography variant="body2" component="span">
                {t('Looking to expand your question set?')}
              </Typography>
              <Button
                variant="text"
                size="small"
                disabled={paraPhrasesLoading}
                onClick={getParaPhrasesBasedOnIntentHandler}
                sx={{ ml: 0.5 }}
              >
                {t('Generate Variations')}
              </Button>
            </Box>
          )}

        <Button variant="outlined" onClick={resetEditKnowledgehandler}>
          {t('Cancel')}
        </Button>

        {props.editMode ? (
          <PermissionWrapper>
            <Button variant="contained" onClick={updateIntenthandler}>
              {t('Save')}
            </Button>
          </PermissionWrapper>
        ) : (
          <PermissionWrapper>
            <Button variant="contained" onClick={createIntenthandler}>
              {buttonText}
            </Button>
          </PermissionWrapper>
        )}
      </Box>
    </Box>
  )
}

export default AddToKnowledge
